import React from 'react';

import { BorderColor as BorderColorIcon, Download as DownloadIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useNavigate } from 'react-router';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';

import { InboxItemRequestItemFragment } from 'src/api';
import { Table, TableColumn, TableRowAction, TableTopAction } from 'src/components/table';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useInboxItemsReportList } from 'src/hooks/reports/useInboxItemsReportList';
import { buildUrl } from 'src/utils/router';

interface RequestsTableInnerProps {
  columns: Array<TableColumn<InboxItemRequestItemFragment>>;
}

export const RequestsTableInner: React.FC<RequestsTableInnerProps> = ({ columns }) => {
  const navigate = useNavigate();

  const { setSearchValue, searchValue } = useSearch();
  const { pagination, setSortOption } = useFilterStorage();

  const { inboxItemsCount, inboxItems, loading } = useInboxItemsReportList();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const navigateToDetails = React.useCallback(
    (inboxItemId: Nullable<string>) => {
      navigate(
        buildUrl(appRoutes.inbox.inboxItem, {
          [RouterEntitiesId.inboxItem]: inboxItemId || '',
        }),
      );
    },
    [navigate],
  );

  const rowActions: Array<TableRowAction<InboxItemRequestItemFragment>> = React.useMemo(
    () => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: inboxItem => navigateToDetails(inboxItem.id),
      },
    ],
    [navigateToDetails],
  );

  return (
    <Table
      dataSource={inboxItems}
      rowCount={inboxItemsCount}
      columns={columns}
      rowActions={rowActions}
      topActions={TOP_ACTIONS}
      pagination={pagination}
      onSearch={setSearchValue}
      onSort={setSortOption}
      loading={loading}
    />
  );
};

const TOP_ACTIONS: Array<TableTopAction> = [
  {
    actionName: 'Download',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onAction: () => {},
    show: true,
    render() {
      return (
        // TODO: enable button when download functionality is implemented
        <IconButton key="Download" disabled>
          <DownloadIcon />
        </IconButton>
      );
    },
  },
];
